'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["collapsable", "filterKey", "getFilterValue", "multi", "options", "SelectComponent", "sortFn", "tooltipMessage", "value"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import localeComparator from 'I18n/utils/localeComparator';
import unescapedText from 'I18n/utils/unescapedText';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import PropTypes from 'prop-types';
import { memo, useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SERVICE, OFFICE_LOCATION } from '../../constants/search/FilterKey';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import { getServices } from '../../slices/serviceCatalogSlice';
import { updateStorefrontFilters } from '../../slices/storefrontFiltersSlice';
import { getCatalogServiceTrackingName } from '../../utils/i18n';
import BasePrerenderFilter from './BasePrerenderFilter';
import { jsx as _jsx } from "react/jsx-runtime";
const defaultGetFilterValue = evt => evt.target.value;
const alphabeticalSortOptions = (optA, optB) => localeComparator(optA.text, optB.text);
const ValueComponent = ({
  children
}) => /*#__PURE__*/_jsx("span", {
  role: "button",
  children: children
});
const BaseFilter = props => {
  const {
      collapsable,
      filterKey,
      getFilterValue = defaultGetFilterValue,
      multi,
      options,
      SelectComponent = Select,
      sortFn = alphabeticalSortOptions,
      tooltipMessage,
      value
    } = props,
    passThroughProps = _objectWithoutPropertiesLoose(props, _excluded);
  const isPrerender = getIsPrerenderUserAgent();
  const dispatch = useDispatch();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const catalogServices = useSelector(getServices);
  const onFilterChange = useCallback(evt => {
    const filterValue = getFilterValue(evt);
    dispatch(updateStorefrontFilters({
      updates: {
        [filterKey]: filterValue
      }
    }));
    trackInteraction('change filter', {
      filter: filterKey,
      value: filterKey === SERVICE ? filterValue.map(serviceId => getCatalogServiceTrackingName(serviceId, catalogServices)) : filterValue
    });
  }, [catalogServices, dispatch, filterKey, getFilterValue, trackInteraction]);
  const sortedOptions = useMemo(() => options && options.sort(sortFn), [options, sortFn]);
  const baseProps = useMemo(() => ({
    anchorType: 'button',
    clearable: !multi,
    multi,
    options: sortedOptions,
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `ecosystem-marketplace-solutions-ui.storefront.filters.${filterKey}.placeholder`
    }),
    value,
    valueComponent: ValueComponent
  }), [filterKey, multi, sortedOptions, value]);
  return /*#__PURE__*/_jsx(FormControl, {
    label: !collapsable && (tooltipMessage ? /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipMessage,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `ecosystem-marketplace-solutions-ui.storefront.filters.${filterKey}.label`
      })
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: `ecosystem-marketplace-solutions-ui.storefront.filters.${filterKey}.label`
    })),
    marginBlockEnd: "0",
    marginInlineStart: filterKey === OFFICE_LOCATION ? '500' : '0',
    "aria-label": collapsable && unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${filterKey}.label`),
    children: isPrerender ? /*#__PURE__*/_jsx(BasePrerenderFilter, Object.assign({
      SelectComponent: SelectComponent,
      filterKey: filterKey
    }, baseProps, passThroughProps)) : /*#__PURE__*/_jsx(SelectComponent, Object.assign({
      onChange: onFilterChange
    }, baseProps, passThroughProps))
  });
};
BaseFilter.propTypes = {
  filterKey: PropTypes.string.isRequired,
  getFilterValue: PropTypes.func,
  collapsable: PropTypes.bool,
  multi: PropTypes.bool,
  options: PropTypes.array,
  SelectComponent: PropTypes.elementType,
  sortFn: PropTypes.func,
  tooltipMessage: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
};
export default /*#__PURE__*/memo(BaseFilter);